import { Badge, Button, Card, Col, Form, Row, Spin, Typography } from "antd";
import { FunctionComponent } from "react";
import { SwitchFormItem } from "../../components/common/SwitchFormItem";
import { DatePickerFormItem } from "../../components/common/DatePickerFormItem";
import { SettingOutlined, SaveOutlined, MailOutlined, UserOutlined, TeamOutlined } from "@ant-design/icons";
import { useAuth } from "../../hooks/AuthContext";
import { UnahtorizedAccess } from "../public/UnahtorizedAccess";
import { useParametresManagement } from "./parametres/hooks/useParametresManagement";

export const Parametres: FunctionComponent = () => {
    const { roles } = useAuth();
    const [form] = Form.useForm();
    const sendMailEnabled = Form.useWatch("sendMailEnabled", { form });

    const {
        isLoading,
        inscriptionEnfantFromDateVisible,
        inscriptionAdulteFromDateVisible,
        onFinish,
        onInscriptionEnfantEnabledChange,
        onInscriptionAdulteEnabledChange,
    } = useParametresManagement(form);

    return roles?.includes("ROLE_ADMIN") ? (
        <div className="centered-content">
            <Form form={form} onFinish={onFinish} layout="vertical" className="container-full-width">
                <Spin spinning={isLoading}>

                    {/* En-tête de page */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        marginTop: 16,
                        marginBottom: 28,
                        paddingBottom: 20,
                        borderBottom: "2px solid #722ed1",
                    }}>
                        <div style={{
                            background: "#722ed1",
                            borderRadius: 12,
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}>
                            <SettingOutlined style={{ color: "#fff", fontSize: 22 }} />
                        </div>
                        <div>
                            <Typography.Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
                                Paramètres de l'application
                            </Typography.Title>
                            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                                Configuration générale du système
                            </Typography.Text>
                        </div>
                    </div>

                    {/* Layout 2 colonnes */}
                    <Row gutter={[24, 24]}>

                        {/* Colonne gauche : Paramètres généraux */}
                        <Col xs={24} lg={12}>
                            <Card
                                title={
                                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{
                                            background: "#e6f4ff",
                                            borderRadius: 6,
                                            padding: "4px 8px",
                                            display: "flex",
                                            alignItems: "center",
                                        }}>
                                            <MailOutlined style={{ color: "#1677ff" }} />
                                        </span>
                                        <span>Paramètres généraux</span>
                                    </span>
                                }
                                extra={sendMailEnabled
                                    ? <Badge status="success" text="Actif" />
                                    : <Badge status="default" text="Inactif" />}
                                bordered
                            >
                                <SwitchFormItem name="sendMailEnabled" label="Activer l'envoi des e-mails" />
                                <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: -8 }}>
                                    Si désactivé, les mails ne sont pas envoyés lors des inscriptions (utile en test).
                                </Typography.Text>
                            </Card>
                        </Col>

                        {/* Colonne droite : Cours enfants + Cours adultes empilées */}
                        <Col xs={24} lg={12}>
                            <Row gutter={[0, 24]}>

                                {/* Carte cours enfants */}
                                <Col xs={24}>
                                    <Card
                                        title={
                                            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <span style={{
                                                    background: "#f6ffed",
                                                    borderRadius: 6,
                                                    padding: "4px 8px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}>
                                                    <TeamOutlined style={{ color: "#52c41a" }} />
                                                </span>
                                                <span>Paramètres cours enfants</span>
                                            </span>
                                        }
                                        extra={
                                            inscriptionEnfantFromDateVisible
                                                ? <Badge status="success" text="Actif" />
                                                : <Badge status="default" text="Inactif" />
                                        }
                                        bordered
                                    >
                                        <Row gutter={[16, 16]}>
                                            <Col xs={24}>
                                                <SwitchFormItem
                                                    name="inscriptionEnfantEnabled"
                                                    label="Activer les inscriptions aux cours enfants"
                                                    onChange={onInscriptionEnfantEnabledChange}
                                                />
                                                <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: -8 }}>
                                                    Active ou désactive le formulaire d'inscription pour les enfants.
                                                </Typography.Text>
                                            </Col>
                                            {inscriptionEnfantFromDateVisible && (
                                                <>
                                                    <Col xs={24} sm={12}>
                                                        <DatePickerFormItem
                                                            name="inscriptionEnfantEnabledFromDate"
                                                            label="A partir du"
                                                            rules={[{ required: true, message: "Date obligatoire" }]}
                                                        />
                                                        <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: -8 }}>
                                                            Date d'ouverture des inscriptions.
                                                        </Typography.Text>
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <SwitchFormItem name="reinscriptionPrioritaire" label="Seulement les réinscriptions" />
                                                        <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: -8 }}>
                                                            Seuls les élèves inscrits l'année précédente peuvent se réinscrire.
                                                        </Typography.Text>
                                                    </Col>
                                                </>
                                            )}
                                        </Row>
                                    </Card>
                                </Col>

                                {/* Carte cours adultes */}
                                <Col xs={24}>
                                    <Card
                                        title={
                                            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <span style={{
                                                    background: "#fff7e6",
                                                    borderRadius: 6,
                                                    padding: "4px 8px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}>
                                                    <UserOutlined style={{ color: "#fa8c16" }} />
                                                </span>
                                                <span>Paramètres cours adultes</span>
                                            </span>
                                        }
                                        extra={
                                            inscriptionAdulteFromDateVisible
                                                ? <Badge status="success" text="Actif" />
                                                : <Badge status="default" text="Inactif" />
                                        }
                                        bordered
                                    >
                                        <Row gutter={[16, 16]}>
                                            <Col xs={24}>
                                                <SwitchFormItem
                                                    name="inscriptionAdulteEnabled"
                                                    label="Activer les inscriptions aux cours adultes"
                                                    onChange={onInscriptionAdulteEnabledChange}
                                                />
                                                <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: -8 }}>
                                                    Active ou désactive le formulaire d'inscription pour les adultes.
                                                </Typography.Text>
                                            </Col>
                                            {inscriptionAdulteFromDateVisible && (
                                                <Col xs={24} sm={12}>
                                                    <DatePickerFormItem
                                                        name="inscriptionAdulteEnabledFromDate"
                                                        label="A partir du"
                                                        rules={[{ required: true, message: "Date obligatoire" }]}
                                                    />
                                                    <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: -8 }}>
                                                        Date à partir de laquelle les inscriptions sont possibles.
                                                    </Typography.Text>
                                                </Col>
                                            )}
                                        </Row>
                                    </Card>
                                </Col>

                            </Row>
                        </Col>

                    </Row>

                    {/* Barre d'actions */}
                    <Card
                        size="small"
                        style={{
                            marginTop: 24,
                            backgroundColor: "#fafafa",
                        }}
                        bodyStyle={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography.Text type="secondary">
                            Cliquer sur Enregistrer pour sauvegarder les modifications.
                        </Typography.Text>
                        <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={isLoading} {...({ testid: "enregistrer-parametres" } as any)}>
                            Enregistrer les paramètres
                        </Button>
                    </Card>

                </Spin>
            </Form>
        </div>
    ) : (
        <UnahtorizedAccess />
    );
};
